import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
  Tooltip,
} from "reactstrap";
import classnames from "classnames";
import { Link, useParams } from "react-router-dom";
import type { Swiper as SwiperType } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode, Navigation, Pagination, Thumbs } from "swiper/modules";
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/navigation";
import "swiper/css/thumbs";
import BreadCrumb from "components/common/BreadCrumb";
import { Product } from "features/Products/types/product.types";
import { getProduct } from "features/Products/services/ProductService";
import moment from "moment";
import { formatCurrency } from "utils/currency";
import { getDocumentTitle } from "utils/helpers";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";

const API_BASE = (import.meta.env.VITE_API_URL || "").replace(/\/$/, "");

const PRODUCTS_BREADCRUMB_BACK = {
  label: "Products",
  path: "/inventory-management/products",
};

function resolveImageSrc(pathOrUrl: string): string {
  const t = pathOrUrl.trim();
  if (!t) return "";
  if (/^https?:\/\//i.test(t)) return t;
  const path = t.replace(/^\//, "");
  if (!API_BASE) return `/${path}`;
  return `${API_BASE}/${path}`;
}

const PricingWidgetList = (props: {
  pricingDetails: {
    id: number;
    icon: string;
    label: string;
    labelDetail: React.ReactNode;
  };
}) => (
  <Col lg={3} sm={6}>
    <div className="p-2 border border-dashed rounded">
      <div className="d-flex align-items-center">
        <div className="avatar-sm me-2">
          <div className="avatar-title rounded bg-transparent text-primary fs-24">
            <i className={props.pricingDetails.icon} />
          </div>
        </div>
        <div className="flex-grow-1 min-w-0">
          <p className="text-muted mb-1 small text-truncate">
            {props.pricingDetails.label}
          </p>
          <h5 className="mb-0 text-truncate">
            {props.pricingDetails.labelDetail}
          </h5>
        </div>
      </div>
    </div>
  </Col>
);

function ViewProductDetails() {
  document.title = getDocumentTitle("Product Details");
  const { can } = useAccess();
  const canEditProduct = can(Permissions.PRODUCT.UPDATE);
  const { id } = useParams();
  const [record, setRecord] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType | null>(null);
  const [editTooltip, setEditTooltip] = useState(false);
  const [customActiveTab, setCustomActiveTab] = useState("1");

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await getProduct(Number(id));
        if (!cancelled && res.success) {
          setRecord(res.data);
        }
      } catch (e) {
        console.warn(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const totalStock = (() => {
    const v = record?.stock?.totalAvailableQty ?? record?.totalAvailableQty;
    return typeof v === "number" && Number.isFinite(v) ? v : null;
  })();
  const stockByLocation = record?.stock?.byLocation ?? [];

  const productDetailsWidgets = record
    ? [
        {
          id: 1,
          icon: "ri-price-tag-3-line",
          label: "Standard rate",
          labelDetail: formatCurrency(record.standardRate ?? 0),
        },
        {
          id: 2,
          icon: "ri-shopping-basket-2-line",
          label: "Purchase cost",
          labelDetail: formatCurrency(record.purchaseCost ?? 0),
        },
        {
          id: 3,
          icon: "ri-stack-line",
          label: "On hand",
          labelDetail: totalStock == null ? "—" : `${totalStock} units`,
        },
      ]
    : [];

  const images = record?.images?.filter(Boolean) ?? [];

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Product Details"
            pageTitle="Inventory Management"
            backTo={PRODUCTS_BREADCRUMB_BACK}
          />
          <p className="text-muted">Loading…</p>
        </Container>
      </div>
    );
  }

  if (!record) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Product Details"
            pageTitle="Inventory Management"
            backTo={PRODUCTS_BREADCRUMB_BACK}
          />
          <p className="text-muted">Product not found.</p>
          <Link
            to="/inventory-management/products"
            className="btn btn-light btn-sm"
          >
            Back to products
          </Link>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Product Details"
          pageTitle="Inventory Management"
          backTo={PRODUCTS_BREADCRUMB_BACK}
        />

        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <Row className="gx-lg-5">
                  <Col xl={4} md={8} className="mx-auto">
                    <div className="product-img-slider sticky-side-div">
                      {images.length > 0 ? (
                        <>
                          <Swiper
                            navigation
                            thumbs={{ swiper: thumbsSwiper }}
                            className="swiper product-thumbnail-slider p-2 rounded bg-light"
                            modules={[Thumbs, Navigation]}
                          >
                            {images.map((img, idx) => (
                              <SwiperSlide key={`${img}-${idx}`}>
                                <img
                                  src={resolveImageSrc(img)}
                                  alt=""
                                  className="img-fluid d-block w-100"
                                  style={{ maxHeight: 320, objectFit: "cover" }}
                                />
                              </SwiperSlide>
                            ))}
                          </Swiper>
                          <div className="product-nav-slider mt-2">
                            <Swiper
                              onSwiper={setThumbsSwiper}
                              slidesPerView={4}
                              freeMode
                              watchSlidesProgress
                              spaceBetween={10}
                              className="swiper product-nav-slider mt-2 overflow-hidden"
                              modules={[FreeMode, Pagination]}
                            >
                              {images.map((img, idx) => (
                                <SwiperSlide
                                  className="rounded"
                                  key={`thumb-${idx}`}
                                >
                                  <div className="nav-slide-item">
                                    <img
                                      src={resolveImageSrc(img)}
                                      alt=""
                                      className="img-fluid d-block rounded"
                                      style={{ height: 72, objectFit: "cover" }}
                                    />
                                  </div>
                                </SwiperSlide>
                              ))}
                            </Swiper>
                          </div>
                        </>
                      ) : (
                        <div className="p-5 rounded bg-light text-center text-muted">
                          <i className="ri-image-2-line display-4 d-block mb-2" />
                          No images
                        </div>
                      )}
                    </div>
                  </Col>

                  <Col xl={8}>
                    <div className="mt-xl-0 mt-5">
                      <div className="d-flex flex-wrap align-items-start gap-2">
                        <div className="flex-grow-1 min-w-0">
                          <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                            <h4 className="mb-0">{record.name}</h4>
                            <span
                              className={`badge rounded-pill bg-${record.isActive ? "success" : "secondary"}-subtle text-${record.isActive ? "success" : "secondary"}`}
                            >
                              {record.isActive ? "Active" : "Inactive"}
                            </span>
                          </div>
                          {record.sku ? (
                            <p className="text-muted mb-2">
                              SKU: <code className="small">{record.sku}</code>
                            </p>
                          ) : null}
                          <div className="hstack gap-3 flex-wrap text-muted small">
                            {record.brand ? (
                              <div>
                                <span className="text-body fw-medium">
                                  {record.brand}
                                </span>
                                {record.manufacturer ? (
                                  <span className="text-muted">
                                    {" "}
                                    · {record.manufacturer}
                                  </span>
                                ) : null}
                              </div>
                            ) : record.manufacturer ? (
                              <span>{record.manufacturer}</span>
                            ) : null}
                            {record.created_at ? (
                              <>
                                <div className="vr d-none d-sm-block" />
                                <div>
                                  Created{" "}
                                  <span className="text-dark fw-medium">
                                    {moment(record.created_at).format(
                                      "MMM D, YYYY",
                                    )}
                                  </span>
                                </div>
                              </>
                            ) : null}
                          </div>
                        </div>
                        {canEditProduct ? (
                          <div className="flex-shrink-0">
                            <Tooltip
                              placement="top"
                              isOpen={editTooltip}
                              target="product-edit-tooltip"
                              toggle={() => setEditTooltip(!editTooltip)}
                            >
                              Edit product
                            </Tooltip>
                            <Link
                              to={`/inventory-management/products/edit/${record.id}`}
                              className="btn btn-light"
                              id="product-edit-tooltip"
                            >
                              <i className="ri-pencil-fill align-bottom" />
                            </Link>
                          </div>
                        ) : null}
                      </div>

                      <Row className="mt-4 g-3">
                        {productDetailsWidgets.map((w) => (
                          <PricingWidgetList pricingDetails={w} key={w.id} />
                        ))}
                      </Row>

                      {record.description ? (
                        <div className="mt-4 text-muted">
                          <h5 className="fs-14">Description</h5>
                          <p className="mb-0">{record.description}</p>
                        </div>
                      ) : null}

                      <div className="product-content mt-5">
                        <h5 className="fs-14 mb-3">Product information</h5>
                        <Nav tabs className="nav-tabs-custom nav-primary">
                          <NavItem>
                            <NavLink
                              style={{ cursor: "pointer" }}
                              className={classnames({
                                active: customActiveTab === "1",
                              })}
                              onClick={() => setCustomActiveTab("1")}
                            >
                              Specifications
                            </NavLink>
                          </NavItem>
                          <NavItem>
                            <NavLink
                              style={{ cursor: "pointer" }}
                              className={classnames({
                                active: customActiveTab === "2",
                              })}
                              onClick={() => setCustomActiveTab("2")}
                            >
                              Stock &amp; audit
                            </NavLink>
                          </NavItem>
                        </Nav>

                        <TabContent
                          activeTab={customActiveTab}
                          className="border border-top-0 p-4"
                          id="product-tab-content"
                        >
                          <TabPane tabId="1">
                            <div className="table-responsive">
                              <table className="table mb-0">
                                <tbody>
                                  <tr>
                                    <th scope="row" style={{ width: 220 }}>
                                      Category
                                    </th>
                                    <td>{record.category?.name ?? "—"}</td>
                                  </tr>
                                  <tr>
                                    <th scope="row">Product type</th>
                                    <td>{record.type?.name ?? "—"}</td>
                                  </tr>
                                  <tr>
                                    <th scope="row">Specification</th>
                                    <td>{record.specification ?? "—"}</td>
                                  </tr>
                                  <tr>
                                    <th scope="row">Material</th>
                                    <td>
                                      {record.catalogMaterial?.name?.trim() ||
                                        record.catalogMaterialName?.trim() ||
                                        "—"}
                                    </td>
                                  </tr>
                                  <tr>
                                    <th scope="row">Unit of measure</th>
                                    <td>
                                      {record.uom?.name ??
                                        record.uom?.code ??
                                        "—"}
                                    </td>
                                  </tr>
                                  <tr>
                                    <th scope="row">Brand</th>
                                    <td>{record.brand ?? "—"}</td>
                                  </tr>
                                  <tr>
                                    <th scope="row">Manufacturer</th>
                                    <td>{record.manufacturer ?? "—"}</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </TabPane>
                          <TabPane tabId="2">
                            <div className="table-responsive">
                              <table className="table mb-0">
                                <tbody>
                                  <tr>
                                    <th scope="row" style={{ width: 220 }}>
                                      Total available
                                    </th>
                                    <td>{totalStock == null ? "—" : totalStock}</td>
                                  </tr>
                                  <tr>
                                    <th scope="row">By location</th>
                                    <td>
                                      {stockByLocation.length === 0 ? (
                                        <span className="text-muted">
                                          No location-wise stock entries.
                                        </span>
                                      ) : (
                                        <div className="table-responsive">
                                          <table className="table table-sm table-bordered mb-0">
                                            <thead className="table-light">
                                              <tr>
                                                <th>Location</th>
                                                <th className="text-end">
                                                  Available qty
                                                </th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {stockByLocation.map((row) => (
                                                <tr
                                                  key={`${row.locationId}-${row.locationName}`}
                                                >
                                                  <td>{row.locationName}</td>
                                                  <td className="text-end">
                                                    {row.availableQty}
                                                  </td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                  <tr>
                                    <th scope="row" style={{ width: 220 }}>
                                      Created
                                    </th>
                                    <td>
                                      {record.created_at
                                        ? moment(record.created_at).format(
                                            "MMM D, YYYY, h:mm A",
                                          )
                                        : "—"}
                                    </td>
                                  </tr>
                                  <tr>
                                    <th scope="row">Last updated</th>
                                    <td>
                                      {record.updated_at
                                        ? moment(record.updated_at).format(
                                            "MMM D, YYYY, h:mm A",
                                          )
                                        : "—"}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </TabPane>
                        </TabContent>
                      </div>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default ViewProductDetails;
